import React, { useState, useMemo } from 'react';
import { downloadCSV } from '../../../../utils/excelUtils';
import { useToast } from '../../context';
import { useLang } from '../../i18n';
import { useSaveFailed } from '../../hooks/useSaveFailed';
import { fmtDateShort, localDateISO } from '../../logic/dates';
import { removeChecklistLog, restoreChecklistLog } from '../../data/checklists';
import { AuditTable } from '../shared/AuditTable';
import { Chips } from '../shared/Toggles';
import { EmptyState } from '../shared/EmptyState';

const statusOf = row => row.status || 'completed';   // legacy rows have no status

/**
 * A checklist's raw history — every logged cycle, sortable, filterable by outcome
 * (completed / pending), exportable as CSV, ✕ to delete a row (undo in the toast).
 */
export function ChecklistLog({ config, log }) {
  const { t, ln } = useLang();
  const notify = useToast();
  const failed = useSaveFailed();
  const verb = t(`checklist.verb.${config.id}`);
  const [filter, setFilter] = useState('all');   // all | completed | pending
  const pendingCount = useMemo(() => log.filter(l => statusOf(l) === 'pending').length, [log]);
  const rows = useMemo(() => log.filter(l => filter === 'all' || statusOf(l) === filter), [log, filter]);

  const columns = useMemo(() => {
    const statusPill = r => {
      const pending = statusOf(r) === 'pending';
      return (
        <span className="do-htable__badge" style={{ '--sc': pending ? 'var(--amber)' : 'var(--green)' }}>
          {pending ? t('checklist.pending') : t('checklist.completedPill', { verb })}
        </span>
      );
    };
    return [
      { key: 'name', label: t('col.item'), cell: r => ln(r.name), className: 'do-htable__item' },
      { key: 'qty', label: t('col.qty'), cell: r => r.qty || '—', className: 'do-htable__date' },
      { key: 'addedDate', label: t('col.added'), cell: r => (r.addedDate ? fmtDateShort(r.addedDate) : '—'), className: 'do-htable__date' },
      { key: 'date', label: t('col.completed'), cell: r => fmtDateShort(r.date), className: 'do-htable__date' },
      { key: 'status', label: t('col.status'), cell: statusPill, sortVal: statusOf },
      { key: 'by', label: t('col.by'), cell: r => r.by || '—', className: 'do-htable__by' },
      ...(config.hasStore ? [{ key: 'store', label: t('col.store'), cell: r => r.store || '—', className: 'do-htable__store' }] : []),
    ];
  }, [config, t, ln, verb]);

  const removeRow = row => {
    removeChecklistLog(row).catch(failed);
    notify(t('checklist.logRemoved', { name: ln(row.name), date: fmtDateShort(row.date) }),
      () => restoreChecklistLog(row).catch(failed));
  };

  const exportCsv = () => {
    if (!rows.length) return;
    downloadCSV(
      ['Item', 'Qty', 'Added', 'Completed', 'Status', 'By', ...(config.hasStore ? ['Store'] : [])],
      rows.map(r => [r.name, r.qty || '', r.addedDate || '', r.date || '', statusOf(r), r.by || '', ...(config.hasStore ? [r.store || ''] : [])]),
      `${config.id}-history-${localDateISO()}`,
    );
  };

  return (
    <>
      <Chips value={filter} onChange={setFilter} options={[
        ['all', t('checklist.filterAll', { n: log.length })],
        ['completed', t('checklist.completedPill', { verb })],
        ['pending', pendingCount ? t('checklist.pendingWithCount', { n: pendingCount }) : t('checklist.pending')],
      ]}>
        <span className="do-checklist__spacer" />
        <button className="do-history__export" onClick={exportCsv} disabled={!rows.length} title={t('checklist.csvTitle')}>{t('common.csv')}</button>
      </Chips>
      {rows.length === 0 ? (
        <EmptyState icon={filter === 'pending' ? '🎉' : '🗓️'}
          title={filter === 'pending' ? t('checklist.noPending') : filter === 'completed' ? t('checklist.nothingMarked', { verb }) : t('checklist.noHistory')} />
      ) : (
        <AuditTable columns={columns} rows={rows} defaultSort={{ key: 'date', dir: 'desc' }}
          rowKey={r => r.id} rowClass={r => (statusOf(r) === 'pending' ? 'do-htable__row--pending' : undefined)}
          onRemove={removeRow} />
      )}
    </>
  );
}
